import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from "@mui/icons-material";
import api from "../../services/api";
import { getHeaders } from "../../utils/apiUtils";

const estadoInicial = {
  nome: "",
  descricao: "",
  empresa_id: "",
  ativo: true,
  modulos: []
};

const normalizarLista = (res) => {
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.dados)) return res.data.dados;
  return [];
};

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [formData, setFormData] = useState(estadoInicial);
  const [profileSelecionado, setProfileSelecionado] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const [profilesRes, empresasRes] = await Promise.allSettled([
        api.get("/profiles", { headers }),
        api.get("/empresas", { headers })
      ]);

      if (profilesRes.status === "fulfilled") {
        setProfiles(normalizarLista(profilesRes.value));
      }

      if (empresasRes.status === "fulfilled") {
        setEmpresas(normalizarLista(empresasRes.value));
      } else {
        throw empresasRes.reason;
      }

      setModulos([]);
    } catch (err) {
      setMessage({ type: "error", text: err.mensagem || err.message || "Erro ao carregar empresas." });
    } finally {
      setLoading(false);
    }
  };

  const carregarModulosEmpresa = async (empresaId) => {
    if (!empresaId) {
      setModulos([]);
      return;
    }

    try {
      const res = await api.get("/profiles/modulos", {
        headers: getHeaders(),
        params: { empresa_id: empresaId }
      });
      const lista = normalizarLista(res);
      setModulos(lista);
      setFormData((prev) => ({
        ...prev,
        modulos: prev.modulos.filter((nomeModulo) => lista.some((modulo) => modulo.st_nome === nomeModulo))
      }));
    } catch (err) {
      setModulos([]);
      setMessage({ type: "error", text: err.mensagem || err.message || "Erro ao carregar modulos da empresa." });
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const abrirFormulario = (profile = null) => {
    const empresaId = profile?.empresa_id || "";
    setProfileSelecionado(profile);
    setFormData(profile ? {
      nome: profile.nome || "",
      descricao: profile.descricao || "",
      empresa_id: empresaId,
      ativo: profile.ativo === true || profile.ativo === 1,
      modulos: Array.isArray(profile.modules)
        ? profile.modules.filter((item) => item.permitido).map((item) => item.modulo)
        : []
    } : estadoInicial);
    setModulos([]);
    carregarModulosEmpresa(empresaId);
    setOpenForm(true);
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    setProfileSelecionado(null);
    setFormData(estadoInicial);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "empresa_id" ? { modulos: [] } : {})
    }));

    if (name === "empresa_id") {
      carregarModulosEmpresa(value);
    }
  };

  const toggleModulo = (nomeModulo) => {
    setFormData((prev) => {
      const existe = prev.modulos.includes(nomeModulo);
      return {
        ...prev,
        modulos: existe
          ? prev.modulos.filter((item) => item !== nomeModulo)
          : [...prev.modulos, nomeModulo]
      };
    });
  };

  const salvarProfile = async () => {
    try {
      const headers = getHeaders();
      const payload = {
        nome: formData.nome,
        descricao: formData.descricao,
        empresa_id: Number(formData.empresa_id),
        ativo: formData.ativo
      };

      const res = profileSelecionado
        ? await api.put(`/profiles/${profileSelecionado.id}`, payload, { headers })
        : await api.post("/profiles", payload, { headers });

      const profile = res.data?.dados || res.data;
      const profileId = profileSelecionado?.id || profile?.id;

      if (profileId) {
        await api.put(`/profiles/${profileId}/modules`, {
          modulos: formData.modulos.map((modulo) => ({ modulo, permitido: true }))
        }, { headers });
      }

      setMessage({ type: "success", text: "Perfil salvo com sucesso." });
      fecharFormulario();
      carregarDados();
    } catch (err) {
      setMessage({ type: "error", text: err.mensagem || err.detalhes || err.message || "Erro ao salvar perfil." });
    }
  };

  const excluirProfile = async (profile) => {
    if (!window.confirm(`Excluir o perfil ${profile.nome}?`)) return;

    try {
      await api.delete(`/profiles/${profile.id}`, { headers: getHeaders() });
      setMessage({ type: "success", text: "Perfil excluido com sucesso." });
      carregarDados();
    } catch (err) {
      setMessage({ type: "error", text: err.mensagem || err.message || "Erro ao excluir perfil." });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Perfis</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestao de perfis compativel com o modelo atual de modulos.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirFormulario()}>
          Novo Perfil
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Modulos</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map((profile) => (
              <TableRow key={profile.id} hover>
                <TableCell>{profile.nome}</TableCell>
                <TableCell>{profile.empresa?.st_nome || profile.empresa_id}</TableCell>
                <TableCell>{Array.isArray(profile.modules) ? profile.modules.filter((item) => item.permitido).length : 0}</TableCell>
                <TableCell>{profile.ativo ? "Ativo" : "Inativo"}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton onClick={() => abrirFormulario(profile)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton onClick={() => excluirProfile(profile)} size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {profiles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {loading ? "Carregando..." : "Nenhum perfil cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openForm} onClose={fecharFormulario} maxWidth="md" fullWidth>
        <DialogTitle>
          {profileSelecionado ? "Editar Perfil" : "Novo Perfil"}
          <IconButton onClick={fecharFormulario} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="Nome" name="nome" value={formData.nome} onChange={handleChange} fullWidth required />
          <TextField label="Descricao" name="descricao" value={formData.descricao} onChange={handleChange} fullWidth multiline minRows={2} />
          <FormControl fullWidth required>
            <InputLabel>Empresa</InputLabel>
            <Select name="empresa_id" value={formData.empresa_id} label="Empresa" onChange={handleChange}>
              {empresas.length === 0 && (
                <MenuItem value="" disabled>
                  {loading ? "Carregando empresas..." : "Nenhuma empresa disponivel"}
                </MenuItem>
              )}
              {empresas.map((empresa) => (
                <MenuItem key={empresa.id_empresa} value={empresa.id_empresa}>{empresa.st_nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={formData.ativo} onChange={handleChange} name="ativo" />}
            label="Ativo"
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Modulos do perfil</Typography>
            <Paper variant="outlined" sx={{ maxHeight: 260, overflow: "auto", p: 1 }}>
              {modulos.map((modulo) => (
                <FormControlLabel
                  key={modulo.id_modulo || modulo.st_nome}
                  control={
                    <Checkbox
                      checked={formData.modulos.includes(modulo.st_nome)}
                      onChange={() => toggleModulo(modulo.st_nome)}
                    />
                  }
                  label={modulo.st_descricao || modulo.st_nome}
                  sx={{ display: "block" }}
                />
              ))}
              {modulos.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {formData.empresa_id ? "Nenhum modulo disponivel para esta empresa." : "Selecione uma empresa para listar os modulos disponiveis."}
                </Typography>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharFormulario}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={salvarProfile}
            disabled={!formData.nome || !formData.empresa_id}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(message)} autoHideDuration={5000} onClose={() => setMessage(null)}>
        {message && <Alert severity={message.type} onClose={() => setMessage(null)}>{message.text}</Alert>}
      </Snackbar>
    </Box>
  );
}
